import React from 'react';
import { IonRouterOutlet } from '@ionic/react';
import { Route } from 'react-router-dom';
import NearestDestinationRedirect from './NearestDestinationRedirect';
import ShareWizardRedirect from './ShareWizardRedirect';
import RecipientPicker from './RecipientPicker';
import ShareComposer from './ShareComposer';
import ShareConfirmStep from './ShareConfirmStep';
import FinalShareConfirmStep from './FinalShareConfirmStep';

/**
 * The URL segments only the wizard has. A session persisted while the flag was
 * off resumes on one of these, as does any link written when the wizard was
 * the flow; written as an alternation so the route is mutually exclusive with
 * the destination routes below, because IonRouterOutlet does not disambiguate
 * overlapping matches by declaration order.
 */
const WIZARD_ONLY_SEGMENTS =
  ':segment(changes|tone|intro-message|reminder-message|update-message)';

/**
 * The garden flow: two routed destinations over the same sharing session the
 * wizard drives, after which the share goes to the outbox.
 *
 * Mounted by `ShareFlowGate` when `topicGardenEnabled` is on, from the same
 * `/topics/:topicId/share` route as the wizard and inside the same session
 * provider and shell, which own the page this outlet renders into. Each
 * destination has a URL of its own, per the repo's
 * deep-linking spec, so a reload or a shared link reconstructs the flow where
 * the sender left it.
 *
 * A new share is sent from the outbox, a modal of its own outside this flow.
 * The two sending destinations are the wizard's own screens, unchanged, for a
 * restored session that already holds a send queue.
 */
const GardenShareRouter: React.FC = () => {
  return (
    <IonRouterOutlet className="share-wizard__outlet">
      <Route
        exact
        path="/topics/:topicId/share"
        component={ShareWizardRedirect}
      />
      <Route
        exact
        path="/topics/:topicId/share/steps/recipients"
        component={RecipientPicker}
      />
      <Route
        exact
        path="/topics/:topicId/share/steps/composer"
        component={ShareComposer}
      />
      <Route
        exact
        path="/topics/:topicId/share/steps/share-confirm"
        component={ShareConfirmStep}
      />
      <Route
        exact
        path="/topics/:topicId/share/steps/final-share-confirm"
        component={FinalShareConfirmStep}
      />
      <Route
        exact
        path={`/topics/:topicId/share/steps/${WIZARD_ONLY_SEGMENTS}`}
        render={() => <NearestDestinationRedirect surface="garden" />}
      />
    </IonRouterOutlet>
  );
};

export default GardenShareRouter;
